import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

type HeightAnimationProps = {
  element: HTMLElement | null | undefined;
  duration?: number;
};

export default function useResizeAnimation(props: HeightAnimationProps) {
  const { element, duration = 330 } = props;

  const resizeObserverRef = useRef<ResizeObserver>();
  const lastHeightRef = useRef(0);
  const isAnimating = useRef(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const activeAnimationCycle = useRef(0);
  const openRef = useRef(true);

  const classes = useMemo(
    () =>
      classNames('s-collapsible-anim', {
        transitioning: isTransitioning,
      }),
    [isTransitioning],
  );

  const animate = useCallback(
    (
      cycleId: number,
      first: number,
      last: number,
      currentTime = Date.now(),
    ) => {
      const diff = last - first;

      if (!diff) return;
      if (!element) return;
      if (cycleId !== activeAnimationCycle.current) return;

      const elapsedTime = Date.now() - currentTime;

      const { current: open } = openRef;
      if (elapsedTime >= duration) {
        if (open) {
          element.removeAttribute('style');
        } else {
          element.style.height = `${last}px`;
        }
        lastHeightRef.current = last;
        isAnimating.current = false;
        resizeObserverRef.current?.observe(element);

        setIsTransitioning(false);

        return;
      } else {
        const ratio = elapsedTime / duration;
        const height = first + ratio * diff;
        lastHeightRef.current = height;
        element.style.height = `${height}px`;
      }

      requestAnimationFrame(() => animate(cycleId, first, last, currentTime));
    },
    [duration, element],
  );

  useEffect(() => {
    if (!element) return;
    const onResize: ResizeObserverCallback = ([entry]) => {
      const first = lastHeightRef.current;
      const [{ blockSize: height }] = entry.borderBoxSize;
      const last = height;
      lastHeightRef.current = height;

      isAnimating.current = true;

      if (Math.abs(first - last) > 1) {
        resizeObserverRef.current?.unobserve(element);
        animate(++activeAnimationCycle.current, first, last);
      }
    };

    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(onResize);
      resizeObserverRef.current.observe(element);
    }

    return () => {
      resizeObserverRef.current?.unobserve(element);
    };
  }, [element, animate]);

  return { classes } as const;
}
